import { ScrollTrigger, gsap } from "@client/gsap";
import { useGSAP } from "@gsap/react";
import React, { useRef } from "react";

import { ServicesHeroBlock as Value } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { RichTextBlock } from "../RichText";
import { ScrollIndicator } from "../ScrollIndicator";

import styles from "./ServicesHeroBlock.module.scss";

export const ServicesHeroBlock = (props: Value) => {
    const heroRef = useRef<HTMLDivElement>(null);
    const headlineRef = useRef<HTMLDivElement>(null);
    const expertiseRef = useRef<HTMLDivElement>(null);
    const expertiseLeftRef = useRef<HTMLDivElement>(null);
    const pillarsRef = useRef<HTMLDivElement>(null);

    // Two animation systems intentionally own opacity on headlineRef to mirror
    // the prototype: IO reveal fires once on entry, ScrollTrigger scrubs out.
    React.useEffect(() => {
        const headline = headlineRef.current;
        if (!headline) return;

        gsap.set(headline, { opacity: 0, y: 28 });

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    gsap.to(entry.target, {
                        opacity: 1,
                        y: 0,
                        duration: 1.1,
                        ease: "power3.out",
                    });
                    observer.unobserve(entry.target);
                });
            },
            { rootMargin: "0px 0px -8% 0px", threshold: 0.12 },
        );

        observer.observe(headline);
        return () => observer.disconnect();
    }, []);

    useGSAP(() => {
        const hero = heroRef.current;
        const headline = headlineRef.current;
        if (!hero || !headline) return;

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: hero,
                start: "top top",
                end: "bottom top",
                scrub: 1,
            },
        });

        tl.to(headline, { opacity: 0, duration: 0.35, ease: "power1.in" }, 0.1);

        tl.set({}, {}, 1);
    }, []);

    // Pin left column and animate pillar opacities on scroll
    useGSAP(() => {
        const section = expertiseRef.current;
        const left = expertiseLeftRef.current;
        const pillarsContainer = pillarsRef.current;
        if (!section || !left || !pillarsContainer) return;

        const pillarEls = pillarsContainer.querySelectorAll<HTMLElement>(
            `.${styles.pillar}`,
        );
        if (pillarEls.length === 0) return;

        const mm = gsap.matchMedia();
        mm.add("(min-width: 1024px)", () => {
            ScrollTrigger.create({
                trigger: section,
                start: "top top",
                end: "bottom bottom",
                pin: left,
                pinSpacing: false,
            });

            pillarEls.forEach((pillar) => {
                gsap.fromTo(
                    pillar,
                    { opacity: 0.25 },
                    {
                        opacity: 1,
                        ease: "none",
                        scrollTrigger: {
                            trigger: pillar,
                            start: "top 75%",
                            end: "top 40%",
                            scrub: true,
                        },
                    },
                );
            });
        });
    }, []);

    return (
        <div className={styles.root}>
            {/* Hero Section */}
            <section className={styles.hero} ref={heroRef}>
                <div className={styles.backgroundContainer}>
                    {props.background_image && (
                        <ImageChooserBlock
                            value={props.background_image}
                            imageAttrs={{ className: styles.backgroundImage }}
                        />
                    )}
                </div>
                <div className={styles.heroContent} ref={headlineRef}>
                    {props.headline && (
                        <RichTextBlock
                            className={styles.headline}
                            value={props.headline}
                            largeHeadings
                        />
                    )}
                </div>
                <div className={styles.scrollIndicatorWrapper}>
                    <ScrollIndicator />
                </div>
            </section>

            {/* Expertise Section */}
            <section className={styles.expertise} ref={expertiseRef}>
                <div className={styles.expertiseGrid}>
                    <div
                        className={styles.expertiseLeft}
                        ref={expertiseLeftRef}
                    >
                        {props.expertise_eyebrow && (
                            <span className={styles.eyebrow}>
                                {props.expertise_eyebrow}
                            </span>
                        )}
                        {props.expertise_headline && (
                            <RichTextBlock
                                className={styles.expertiseHeadline}
                                value={props.expertise_headline}
                                largeHeadings
                            />
                        )}
                        {props.expertise_body && (
                            <RichTextBlock
                                className={styles.expertiseBody}
                                value={props.expertise_body}
                            />
                        )}
                    </div>
                    <div className={styles.expertisePillars} ref={pillarsRef}>
                        {props.pillars && (
                            <StreamField
                                value={props.pillars}
                                components={{
                                    pillar: ({ value }) => (
                                        <div className={styles.pillar}>
                                            <h3
                                                className={styles.pillarHeading}
                                            >
                                                {value.heading}
                                            </h3>
                                            {value.body && (
                                                <RichTextBlock
                                                    className={
                                                        styles.pillarBody
                                                    }
                                                    value={value.body}
                                                />
                                            )}
                                        </div>
                                    ),
                                }}
                            />
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};
